import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useGo } from "@/components/jf/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COPY = {
  paid: {
    kicker: "Payment Received",
    title: <>Your advance is <em className="text-brass-soft font-light">with us.</em></>,
    body: "₹5,000 advance received — fully adjusted in your final invoice. Our team will call you within 24 hours to confirm everything.",
  },
  pending: {
    kicker: "One Moment",
    title: <>Confirming <em className="text-brass-soft font-light">your payment…</em></>,
    body: "Stripe is still confirming. If the amount was debited, it will reflect shortly — you can safely close this page.",
  },
  cancelled: {
    kicker: "Payment Cancelled",
    title: <>No charge <em className="text-brass-soft font-light">was made.</em></>,
    body: "Your booking still stands — our team will call you within 24 hours, and you can pay the advance then if you prefer.",
  },
};

export default function PaymentResultPage({ type }) {
  const [params] = useSearchParams();
  const go = useGo();
  const [state, setState] = useState(type === "cancel" ? "cancelled" : "checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (type !== "success" || !sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setState("paid");
          return;
        }
      } catch {}
      if (++tries < 8) timer = setTimeout(poll, 2500);
      else setState("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [type, sessionId]);

  const copy = COPY[state] || COPY.pending;

  return (
    <main data-testid="payment-result" className="bg-oxblood min-h-screen flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="text-center max-w-xl"
      >
        <img src="/logo-ivory.png" alt="Jaipur Furniture" className="h-16 w-auto mx-auto mb-10" />
        <p className="text-brass-soft text-xs tracking-[0.45em] uppercase mb-6">{copy.kicker}</p>
        <h1 className="font-display text-ivory text-4xl md:text-6xl leading-tight">{copy.title}</h1>
        <p className="mt-8 text-ivory/60 font-light leading-relaxed">{copy.body}</p>
        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <button
            data-testid="payment-home-btn"
            onClick={() => go("top")}
            className="bg-ivory text-oxblood-ink hover:bg-brass-soft px-9 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-400"
          >
            Back to Home
          </button>
          {state === "cancelled" ? (
            <button
              data-testid="payment-retry-btn"
              onClick={() => go("book")}
              className="border border-ivory/40 text-ivory hover:border-brass hover:text-brass-soft px-9 py-4 text-xs tracking-[0.25em] uppercase transition-colors duration-400"
            >
              Return to Booking
            </button>
          ) : null}
        </div>
      </motion.div>
    </main>
  );
}
